import {ReactNode, useCallback, useEffect, useState} from 'react';

// eslint-disable-next-line no-restricted-imports
import BaseSelect, {Props, components, StylesConfig, ValueContainerProps} from 'react-select';

export const DropDownValueContainer = <T extends object>({children, ...props}: ValueContainerProps<T, false>) => (
  <components.ValueContainer {...props}>{children}</components.ValueContainer>
);

type DropDownProps<T extends object> = Props<T, false> & {
  name: string;
  children?: ReactNode;
  styles?: StylesConfig<T, false>;
  omitDefaultStyles?: boolean;
};

const createDefaultDropdownStyles: <T extends object>(styles: StylesConfig<T, false>, isOpen: boolean) => StylesConfig<T, false> = (styles, isOpen) => ({
  control: (base, controlProps) => ({
    ...(styles?.control?.(base, controlProps) || base),
    boxShadow: 'none',
    borderColor: '#cccccc !important',
    cursor: 'pointer',
  }),
  dropdownIndicator: base => ({
    ...base,
    transition: 'all .2s ease',
    transform: isOpen ? 'rotate(180deg)' : '',
  }),
  option: (base, optionProps) => ({
    ...(styles?.option?.(base, optionProps) || base),
    backgroundColor: optionProps.isSelected ? '#F8F5F2' : 'white',
    color: '#0a3847',
  }),
  indicatorSeparator: () => ({
    backgroundColor: 'inherit',
  }),
});

const DropDown = <T extends object>({children, styles, name, omitDefaultStyles, ...props}: DropDownProps<T>) => {
  if (!name) {
    throw new Error('to support ssr with react-select components, please pass a name to each instance of Select.');
  }

  const [isOpen, setIsOpen] = useState(false);

  const dropDownElId = `${name}_dropdown`;

  useEffect(() => {
    const dropdownElement = document.getElementById(dropDownElId);
    const isAriaExpanded = dropdownElement?.getAttribute?.('aria-expanded');

    if (!isAriaExpanded || !isOpen) {
      dropdownElement?.setAttribute('aria-expanded', 'false');
    }
    if (isOpen) {
      dropdownElement?.setAttribute('aria-expanded', 'true');
    }
  }, [dropDownElId, isOpen]);

  const onMenuOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const onMenuClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const myStyles = omitDefaultStyles ? styles : createDefaultDropdownStyles(styles, isOpen);

  return (
    <BaseSelect
      {...props}
      id={dropDownElId}
      name={name}
      isOpen={isOpen}
      onMenuOpen={onMenuOpen}
      onMenuClose={onMenuClose}
      styles={myStyles}
    >
      {children}
    </BaseSelect>
  );
};

export default DropDown;
